import React from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import movieNightImage from '@app/admin/titles/movie-night.svg';
import {Link} from 'react-router-dom';
import {Lien} from '@app/titles/models/lien';
import {ColumnConfig} from '@common/datatable/column-config';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {FormattedNumber} from '@ui/i18n/formatted-number';
import {EditIcon} from '@ui/icons/material/Edit';
import {EnumLinkQualityChip} from '@ui/i18n/enum-quality';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {FormattedBytes} from '@ui/i18n/formatted-bytes';
import {FormattedRelativeTime} from '@ui/i18n/formatted-relative-time';
import {Title} from '@app/titles/models/title';
import {useAuth} from '@common/auth/use-auth';
import {
  DownloadPartsItemsAction,
  DownloadSelectedItemsAction,
} from '@common/datatable/page/download-selected-items-action';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {NfoDialog} from '@app/liens/nfo-dialog';
import {InfoIcon} from '@ui/icons/material/Info';
import {
  FetchLinksDataResponse,
  LinksIndexPageFilters,
} from '@common/http/links-filters';
import {useLinkFiltersData} from '@app/liens/use-link-filters';
import {DownloadDialog} from '@app/liens/download-dialog';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {Button} from '@ui/buttons/button';
import {ReportLinkDialog} from '@app/reports/report-link-dialog';
import {FlagIcon} from '@ui/icons/material/Flag';

export function LiensListDatatablePage({
  title,
  filtersData,
  season,
}: {
  title: Title;
  filtersData: FetchLinksDataResponse;
  season?: number;
}) {
  const {hasPermission, user} = useAuth();
  const filtersDefault = useLinkFiltersData();
  const hostsArray = Object.values(filtersDefault?.hosts || {});

  const filters = LinksIndexPageFilters({
    data: filtersData,
    filters: filtersDefault,
    title,
  });

  const actions: ColumnConfig<Lien>[] = [
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      minWidth: 'min-w-100',
      visibleInMode: 'all',
      align: 'end',
      body: lien => (
        <div className={'inline-flex'}>
          <DialogTrigger type="modal">
            <Tooltip
              label={
                <Trans
                  message="Download on :host"
                  values={{
                    host: hostsArray.find(h => h.id === lien.id_host)?.domain,
                  }}
                />
              }
            >
              <IconButton size="md" className="text-muted">
                <img
                  alt={hostsArray.find(h => h.id === lien.id_host)?.domain}
                  className={'max-h-20'}
                  src={
                    hostsArray.find(h => h.id === lien.id_host)?.icon
                      ? hostsArray.find(h => h.id === lien.id_host)?.icon
                      : 'https://www.google.com/s2/favicons?domain=' +
                        hostsArray.find(h => h.id === lien.id_host)?.domain
                  }
                />
              </IconButton>
            </Tooltip>
            {lien.total_parts && lien.total_parts > 0 ? (
              <DownloadPartsItemsAction id={lien.id} />
            ) : (
              <DownloadDialog user={user || undefined} id={lien.id} />
            )}
          </DialogTrigger>
          <DialogTrigger type="modal">
            <IconButton size="md" className="text-muted">
              <InfoIcon />
            </IconButton>
            <NfoDialog id={lien.id} />
          </DialogTrigger>
          <DialogTrigger type="modal">
            <IconButton size="md" className="text-muted">
              <FlagIcon />
            </IconButton>
            <ReportLinkDialog link={lien} modelType={'Link'} />
          </DialogTrigger>
          {(hasPermission('liens.update') ||
            (hasPermission('liens.create') &&
              user?.username == lien.id_user)) && (
            <Link
              to={`/admin/liens/${lien.title_id}/${lien.id}/edit`}
              className="text-muted"
            >
              <Tooltip label={<Trans message="Edit" />}>
                <IconButton size="md">
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Link>
          )}
        </div>
      ),
    },
  ];
  const commonColumns: ColumnConfig<Lien>[] = [
    {
      key: 'taille',
      visibleInMode: 'all',
      minWidth: 'min-w-100',
      header: () => <Trans message="Size" />,
      body: lien => (
        <div>
          {lien.taille && lien.taille > 0 ? (
            <FormattedBytes bytes={lien.taille} />
          ) : (
            '-'
          )}{' '}
          {lien.total_parts && lien.total_parts > 0 ? (
            <span>({lien.total_parts} Parts)</span>
          ) : (
            ''
          )}
        </div>
      ),
    },
    {
      key: 'qualite',
      allowsSorting: true,
      visibleInMode: 'all',
      header: () => <Trans message="Quality" />,
      body: lien => <EnumLinkQualityChip quality={lien.qual.qual} />,
    },
    {
      key: 'langues_compact',
      visibleInMode: 'all',
      header: () => <Trans message="Languages" />,
      body: lien => {
        const langues = lien?.langues_compact || [];
        const displayedLangues = langues.slice(0, 2);
        const remainingLangues = langues
          .slice(2)
          .map(lang => lang.name)
          .join(', ');

        return (
          <>
            <ChipList radius="rounded" className="z-50" size="xs">
              <>
                {displayedLangues?.map(lang => (
                  <Chip key={lang.id} selectable>
                    <Trans message={lang.name} />
                  </Chip>
                ))}
                {langues?.length > 2 && (
                  <DialogTrigger type="modal">
                    <Button variant="link">
                      +{lien?.langues_compact.length} <Trans message={'more'} />
                    </Button>
                    <PreviewDialog remainingLangues={remainingLangues} />
                  </DialogTrigger>
                )}
              </>
            </ChipList>
          </>
        );
      },
    },
    {
      key: 'subs_compact',
      visibleInMode: 'all',
      header: () => <Trans message="Subtitles" />,
      body: lien => {
        const langues = lien?.subs_compact || [];
        const displayedLangues = langues.slice(0, 2);
        const remainingLangues = langues
          .slice(2)
          .map(lang => lang.name)
          .join(', ');

        return (
          <>
            <ChipList radius="rounded" className="z-50" size="xs">
              <>
                {displayedLangues?.map(lang => (
                  <Chip key={lang.id} selectable>
                    <Trans message={lang.name} />
                  </Chip>
                ))}
                {langues?.length > 2 && (
                  <DialogTrigger type="modal">
                    <Button variant="link">
                      +{lien?.subs_compact.length} <Trans message={'more'} />
                    </Button>
                    <PreviewDialog remainingLangues={remainingLangues} />
                  </DialogTrigger>
                )}
              </>
            </ChipList>
          </>
        );
      },
    },
    {
      key: 'Uploader',
      visibleInMode: 'regular',
      allowsSorting: false,
      header: () => <Trans message="Uploader" />,
      body: lien => (
        <Link to={`/get-user/${lien.id_user}`}>{lien.id_user}</Link>
      ),
    },
    {
      key: 'created_at',
      allowsSorting: true,
      visibleInMode: 'regular',
      header: () => <Trans message="Added" />,
      body: lien =>
        lien.updated_at ? <FormattedRelativeTime date={lien.created_at} /> : '',
    },
  ];

  const LiensListDatatableColumns: ColumnConfig<Lien>[] = [
    ...commonColumns,
    ...actions,
  ];
  const LiensListDatatableColumnsSerie: ColumnConfig<Lien>[] = [
    {
      key: 'episode',
      allowsSorting: true,
      minWidth: 'min-w-100',
      visibleInMode: 'all',
      header: () => <Trans message="Episode" />,
      body: lien =>
        lien.full_saison ? (
          <Trans message={'Full season'} />
        ) : (
          <FormattedNumber value={lien.episode} />
        ),
    },
    ...commonColumns,
    ...actions,
  ];

  return (
    <DataTablePage
      key={`${title.id}-${season}-ddl`}
      endpoint={'liens'}
      enableSearch={false}
      filters={filters}
      pinnedFilters={['id_host', 'qualite', 'langues']}
      queryParams={{
        title_id: title.id,
        loader: 'linksdl',
        season: season,
      }}
      columns={
        title.is_series
          ? LiensListDatatableColumnsSerie
          : LiensListDatatableColumns
      }
      enableSelection={user?.IsPremium}
      selectedActions={<DownloadSelectedItemsAction />}
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={movieNightImage}
          title={<Trans message="No links have been created yet" />}
          filteringTitle={<Trans message="No matching links" />}
        />
      }
    />
  );
}

function PreviewDialog({remainingLangues}: {remainingLangues: string}) {
  return (
    <Dialog size="sm" className="flex flex-col">
      <DialogBody>{remainingLangues}</DialogBody>
    </Dialog>
  );
}
